import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Pencil, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { EntityFormDialog } from "@/components/EntityFormDialog";

const StockControls = ({ endpoint, item, onChange, prefix }) => {
  const change = async (delta) => {
    try {
      const { data } = await api.post(`/${endpoint}/${item.id}/stock`, { delta });
      onChange(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  return (
    <div className="inline-flex items-center rounded-md border border-slate-200">
      <button data-testid={`${prefix}-stock-decrement-${item.id}`} onClick={() => change(-1)} className="px-2 py-1 text-slate-500 hover:bg-slate-100" disabled={item.quantita <= 0}><Minus className="h-3.5 w-3.5" /></button>
      <button data-testid={`${prefix}-stock-increment-${item.id}`} onClick={() => change(1)} className="border-l border-slate-200 px-2 py-1 text-slate-500 hover:bg-slate-100"><Plus className="h-3.5 w-3.5" /></button>
    </div>
  );
};

export default function EntityPage({ overline, title, endpoint, prefix, columns, fields, newLabel, singular, stock, onRowClick, deleteDescription }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/${endpoint}`, { params: { q } });
      setItems(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/${endpoint}/${id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const replace = (upd) => setItems((list) => list.map((i) => (i.id === upd.id ? upd : i)));

  return (
    <div>
      <PageHeader overline={overline} title={title}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid={`${prefix}-search-input`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="w-56 bg-white pl-9" />
        </div>
        <Button data-testid={`${prefix}-new-button`} onClick={() => { setEditing(null); setOpen(true); }} className="bg-orange-600 hover:bg-orange-700">
          <Plus className="mr-1 h-4 w-4" /> {newLabel}
        </Button>
      </PageHeader>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm fade-up" style={{ animationDelay: "80ms" }}>
        <Table data-testid={`${prefix}-table`}>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {columns.map((c) => <TableHead key={c.key} className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{c.label}</TableHead>)}
              <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`${prefix}-row-${it.id}`} className={onRowClick ? "cursor-pointer" : ""} onClick={() => onRowClick?.(it)}>
                {columns.map((c) => <TableCell key={c.key} className="text-sm">{c.render ? c.render(it) : it[c.key] || "—"}</TableCell>)}
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {stock && <StockControls endpoint={endpoint} item={it} onChange={replace} prefix={prefix} />}
                    <Button variant="ghost" size="icon" data-testid={`${prefix}-edit-${it.id}`} onClick={() => { setEditing(it); setOpen(true); }} className="text-slate-400 hover:text-slate-900">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete testId={`${prefix}-delete-${it.id}`} onConfirm={() => remove(it.id)} description={deleteDescription} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="py-16 text-center text-sm text-slate-500" data-testid={`${prefix}-empty`}>
                  Nessun elemento trovato.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <EntityFormDialog
        open={open} onOpenChange={setOpen} endpoint={endpoint} fields={fields} item={editing} prefix={`${prefix}-form`}
        title={editing ? `Modifica ${singular}` : newLabel} onSaved={load}
      />
    </div>
  );
}
